/**
 * Concurrency helpers v1.0.0
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * IIFE providing concurrency primitives (must be defer, not module).
 *
 * Helpers:
 * - latestOnly(key, asyncFn): per-key Last-Write-Wins runner with AbortController
 *
 * WHY IIFE (not ES Module):
 * Used by alpine-stores.js IIFE, which has to run before Alpine fires alpine:init.
 * ES Modules execute AFTER defer scripts. Same constraint as events / formatMoney.
 *
 * ESM bridge: assets/concurrency-bridge.js (re-exports from window.Theme).
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 */

(function () {
  'use strict';

  window.Theme = window.Theme || {};

  // Per-key runtime state. Lazily created on first call for a given key.
  // Shape: { [key]: { version: number, controller: AbortController | null } }
  const state = Object.create(null);

  /**
   * Run an async fn under per-key Last-Write-Wins semantics.
   *
   * Each call increments a version counter scoped to `key` and aborts the
   * previous in-flight request for that key (independent from other keys).
   * The returned promise resolves to:
   *   - `{ result, isLatest: true }`  — this call's response is the latest
   *   - `{ result, isLatest: false }` — a newer call for this key superseded
   *                                     this one before it resolved
   *   - `null`                        — this call was aborted (AbortError);
   *                                     callers should treat as "no-op"
   *
   * Non-abort errors propagate (re-thrown).
   *
   * Contract:
   *   asyncFn receives a single argument: the AbortSignal scoped to this call.
   *   asyncFn MUST forward that signal to fetch (or any cancellable op) so
   *   the abort actually cancels the work, not just the bookkeeping.
   *
   * Per-key isolation:
   *   latestOnly('cart', ...) and latestOnly('discount', ...) do NOT share
   *   queues. Aborting one key's in-flight request leaves others untouched.
   *
   * Usage (in cart store):
   *   const outcome = await Theme.latestOnly('cart', (signal) =>
   *     fetch('/cart/change.js', { method: 'POST', signal, body }).then(r => r.json())
   *   );
   *   if (!outcome) return; // aborted
   *   if (outcome.isLatest) morphSections(outcome.result.sections);
   *
   * @param {string} key
   * @param {(signal: AbortSignal) => Promise<unknown>} asyncFn
   * @returns {Promise<{ result: unknown, isLatest: boolean } | null>}
   */
  async function latestOnly(key, asyncFn) {
    if (typeof key !== 'string' || !key) {
      throw new TypeError('latestOnly: key must be a non-empty string');
    }
    if (typeof asyncFn !== 'function') {
      throw new TypeError('latestOnly: asyncFn must be a function');
    }

    // Initialize or fetch key state
    if (!state[key]) {
      state[key] = { version: 0, controller: null };
    }
    const keyState = state[key];

    // Increment version counter for this call
    const thisVersion = ++keyState.version;

    // Abort the previous in-flight request if one exists
    if (keyState.controller) {
      keyState.controller.abort();
    }

    // Create new AbortController for this request
    keyState.controller = new AbortController();

    try {
      // Execute the async function with the abort signal
      const result = await asyncFn(keyState.controller.signal);

      // Only return result if this is still the latest version
      // (a newer call may have superseded us while we were in flight)
      const isLatest = thisVersion === keyState.version;

      return {
        result: isLatest ? result : null,
        isLatest
      };
    } catch (err) {
      // If the request was aborted (signal fired), resolve to null silently
      if (err.name === 'AbortError') {
        return null;
      }

      // For all other errors, propagate them to the caller
      throw err;
    }
  }

  window.Theme.latestOnly = latestOnly;

  // For tests / debugging.
  window.Theme._concurrencyState = state;
})();
